import React, {useEffect, useMemo, useRef, useState} from "react";
import {Box, IconButton, InputBase, Stack, Typography} from "@mui/material";
import {HugeiconsIcon} from "@hugeicons/react";
import {Cancel01Icon, CornerDownLeftIcon, Search01Icon} from "@hugeicons/core-free-icons";
import {NodeTypeIcon} from "../../../model/ModelData";
import {hexToRgba} from "../../../nodes/nodeTheme";

// Same palette as the node menu on the canvas
const ACCENT = "#6366F1";
const INK = "#1E293B";
const MUTED = "#64748B";
const BORDER = "rgba(15, 23, 42, 0.08)";
const MAX_RESULTS = 8;

const isMac = typeof navigator !== "undefined" && /Mac|iPhone|iPad/.test(navigator.platform || "");

// greet is an array in the node model, so take the first line for the label
const nodeLabel = (node) => {
  const greet = node?.data?.greet;
  return node?.data?.name || (Array.isArray(greet) ? greet[0] : greet) || node?.type;
};

const typeLabel = (type) => type
  ? type.replaceAll("_", " ").replace(/^\w/, c => c.toUpperCase())
  : "Node";

// The label with the matched part of the query highlighted
const Highlight = ({text, query}) => {
  const value = String(text || "");
  const index = query ? value.toLowerCase().indexOf(query.toLowerCase()) : -1;
  if (index < 0) return value;
  return (
    <>
      {value.slice(0, index)}
      <Box component={"mark"} sx={{bgcolor: hexToRgba(ACCENT, 0.16), color: ACCENT, borderRadius: "3px", px: "1px"}}>
        {value.slice(index, index + query.length)}
      </Box>
      {value.slice(index + query.length)}
    </>
  );
};

const Kbd = ({children}) => (
  <Box component={"kbd"} sx={{
    px: 0.625, py: 0.125, borderRadius: "5px", fontSize: 10.5, fontFamily: "inherit", fontWeight: 600, lineHeight: 1.6,
    color: MUTED, bgcolor: "#F1F5F9", border: `1px solid ${BORDER}`, whiteSpace: "nowrap",
  }}>
    {children}
  </Box>
);

// Floating search over the canvas nodes (name, greet, type).
// nodes: react flow nodes. onSelect(node): a result was picked (the canvas centers on it).
// Ctrl / Cmd + K focuses it, arrows move through the results, Enter picks, Escape clears.
export const FlowNodeSearch = ({nodes, onSelect}) => {
  const [query, setQuery] = useState("");
  const [active, setActive] = useState(0);
  const [focused, setFocused] = useState(false);
  const inputRef = useRef(null);
  const listRef = useRef(null);

  const trimmed = query.trim();

  const matches = useMemo(() => {
    if (!trimmed) return [];
    const needle = trimmed.toLowerCase();
    return (nodes || []).filter((node) => {
      const greet = node?.data?.greet;
      return [node?.data?.name, Array.isArray(greet) ? greet.join(" ") : greet, node?.data?.node?.type]
        .filter(Boolean).join(" ").toLowerCase().includes(needle);
    });
  }, [nodes, trimmed]);
  const results = matches.slice(0, MAX_RESULTS);

  useEffect(() => setActive(0), [trimmed]);

  useEffect(() => {
    listRef.current?.querySelector(`[data-index="${active}"]`)?.scrollIntoView({block: "nearest"});
  }, [active]);

  useEffect(() => {
    const onKey = (event) => {
      if ((event.ctrlKey || event.metaKey) && event.key.toLowerCase() === "k") {
        event.preventDefault();
        inputRef.current?.focus();
        inputRef.current?.select();
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  const pick = (node) => {
    onSelect(node);
    setQuery("");
    inputRef.current?.blur();
  };

  const onKeyDown = (event) => {
    if (event.key === "Escape") {
      event.preventDefault();
      if (query) setQuery("");
      else inputRef.current?.blur();
    } else if (event.key === "ArrowDown" && results.length) {
      event.preventDefault();
      setActive(current => (current + 1) % results.length);
    } else if (event.key === "ArrowUp" && results.length) {
      event.preventDefault();
      setActive(current => (current - 1 + results.length) % results.length);
    } else if (event.key === "Enter") {
      event.preventDefault();
      if (results[active]) pick(results[active]);
    }
  };

  const open = Boolean(trimmed) && focused;

  return (
    <Box
      onMouseDown={(event) => event.stopPropagation()}
      onClick={(event) => event.stopPropagation()}
      sx={{position: "relative", width: {xs: 280, md: focused || query ? 380 : 320}, transition: "width 160ms ease"}}
    >
      <Stack
        data-tour="flow-search"
        direction={"row"}
        alignItems={"center"}
        gap={1}
        onClick={() => inputRef.current?.focus()}
        sx={{
          height: 42, px: 1.5, borderRadius: 10, bgcolor: "#FFFFFF", cursor: "text",
          border: `1px solid ${focused ? hexToRgba(ACCENT, 0.45) : BORDER}`,
          boxShadow: focused
            ? `0 8px 24px rgba(15, 23, 42, 0.1), 0 0 0 4px ${hexToRgba(ACCENT, 0.12)}`
            : "0 4px 14px rgba(15, 23, 42, 0.06)",
          transition: "border-color 160ms ease, box-shadow 160ms ease",
          "&:hover": {borderColor: focused ? hexToRgba(ACCENT, 0.45) : "rgba(15, 23, 42, 0.14)"},
        }}
      >
        <Box sx={{display: "flex", color: focused ? ACCENT : MUTED, transition: "color 160ms ease", flexShrink: 0}}>
          <HugeiconsIcon icon={Search01Icon} size={17}/>
        </Box>

        <InputBase
          inputRef={inputRef}
          fullWidth
          value={query}
          onChange={(event) => setQuery(event.target.value)}
          onKeyDown={onKeyDown}
          onFocus={() => setFocused(true)}
          onBlur={() => setFocused(false)}
          placeholder={"Search nodes…"}
          autoComplete={"off"}
          sx={{fontSize: 13.5, color: INK, "& input::placeholder": {color: MUTED, opacity: 0.8}}}
        />

        {query ? (
          <IconButton
            size={"small"}
            aria-label={"Clear search"}
            onMouseDown={(event) => event.preventDefault()}
            onClick={() => setQuery("")}
            sx={{width: 24, height: 24, flexShrink: 0, color: MUTED, "&:hover": {color: INK, bgcolor: "#F1F5F9"}}}
          >
            <HugeiconsIcon icon={Cancel01Icon} size={14}/>
          </IconButton>
        ) : (
          <Kbd>{isMac ? "⌘ K" : "Ctrl K"}</Kbd>
        )}
      </Stack>

      {open && (
        <Box
          onMouseDown={(event) => event.preventDefault()}
          sx={{
            position: "absolute", top: "calc(100% + 6px)", left: 0, right: 0, overflow: "hidden",
            bgcolor: "#FFFFFF", borderRadius: "14px", border: `1px solid ${BORDER}`,
            boxShadow: "0 16px 40px rgba(15, 23, 42, 0.14)",
          }}
        >
          {results.length > 0 ? (
            <>
              <Typography sx={{px: 1.5, pt: 1, pb: 0.5, fontSize: 10.5, fontWeight: 700, letterSpacing: "0.08em", color: MUTED}}>
                {matches.length > MAX_RESULTS ? `${MAX_RESULTS} OF ${matches.length} NODES` : `${matches.length} NODE${matches.length === 1 ? "" : "S"}`}
              </Typography>
              <Box ref={listRef} sx={{maxHeight: 320, overflowY: "auto", px: 0.5, pb: 0.5}}>
                {results.map((node, index) => {
                  const type = node?.data?.node?.type;
                  const Icon = NodeTypeIcon[type];
                  const selected = index === active;
                  return (
                    <Stack
                      key={node.id}
                      data-index={index}
                      direction={"row"}
                      alignItems={"center"}
                      gap={1.25}
                      onMouseEnter={() => setActive(index)}
                      onClick={() => pick(node)}
                      sx={{
                        px: 1, py: 0.875, borderRadius: "10px", cursor: "pointer",
                        bgcolor: selected ? hexToRgba(ACCENT, 0.08) : "transparent",
                      }}
                    >
                      <Box sx={{
                        width: 30, height: 30, borderRadius: "9px", flexShrink: 0, display: "flex", alignItems: "center",
                        justifyContent: "center", color: ACCENT, bgcolor: hexToRgba(ACCENT, selected ? 0.16 : 0.1),
                      }}>
                        {Icon ? <Icon size={16}/> : <HugeiconsIcon icon={Search01Icon} size={14}/>}
                      </Box>
                      <Box sx={{flex: 1, minWidth: 0}}>
                        <Typography noWrap sx={{fontSize: 13, fontWeight: 600, color: INK}}>
                          <Highlight text={nodeLabel(node)} query={trimmed}/>
                        </Typography>
                        <Typography noWrap sx={{fontSize: 11.5, color: MUTED}}>{typeLabel(type)}</Typography>
                      </Box>
                      {selected && <Box sx={{color: MUTED, display: "flex", flexShrink: 0}}>
                        <HugeiconsIcon icon={CornerDownLeftIcon} size={14}/>
                      </Box>}
                    </Stack>
                  );
                })}
              </Box>
              <Stack direction={"row"} alignItems={"center"} gap={1.5}
                     sx={{px: 1.5, py: 0.75, borderTop: `1px solid ${BORDER}`, bgcolor: "#F8FAFC"}}>
                {[["↑ ↓", "navigate"], ["Enter", "go to node"], ["Esc", "clear"]].map(([key, label]) => (
                  <Stack key={key} direction={"row"} alignItems={"center"} gap={0.5}>
                    <Kbd>{key}</Kbd>
                    <Typography sx={{fontSize: 11, color: MUTED}}>{label}</Typography>
                  </Stack>
                ))}
              </Stack>
            </>
          ) : (
            <Stack alignItems={"center"} gap={0.5} sx={{px: 2, py: 2.5}}>
              <Box sx={{color: MUTED, display: "flex", opacity: 0.7}}><HugeiconsIcon icon={Search01Icon} size={20}/></Box>
              <Typography sx={{fontSize: 13, fontWeight: 600, color: INK}}>No nodes found</Typography>
              <Typography sx={{fontSize: 12, color: MUTED, textAlign: "center"}}>
                Nothing matches “{trimmed}” by name, text or type.
              </Typography>
            </Stack>
          )}
        </Box>
      )}
    </Box>
  );
};
